import { Injectable } from '@angular/core';
import { type Event } from '@bugsnag/core/types';
import Bugsnag from '@bugsnag/js';

/**
 * A list of errors that we send to Bugsnag.
 * The list is used to group the errors better in Bugsnag.
 * Must match 'Group by error class' at https://app.bugsnag.com/settings/reconcept-1/projects/portfolio-web
 */
export type BugsnagErrorClass =
  | 'Error' // generic error, default
  | 'CodeError' // unexpected error in code, from ErrorSinkService
  | 'CustomError' // expected custom error, from the codebase
  | 'GraphQlQuery' // error in graphql query, from ApolloGraphqlErrorService
  | 'GraphQlMutation' // error in graphql mutation, from ApolloGraphqlErrorService
  | 'ApolloError' // error in Apollo, from ApolloGraphqlErrorService
  | 'Timeout' // error in GraphQL, from ApolloGraphqlErrorService
  | 'NodeRedirectError' // error in redirecting to a node, from NodeRedirectService, AppNodeRedirectService
  | 'PortfolioDetailContextError'; // error in PortfolioDetailContext, from PortfolioDetailContext

@Injectable({ providedIn: 'root' })
export class BugsnagClient {
  notify(appError: Error, metaData?: Record<string, Record<string, any>>, errorClass: BugsnagErrorClass = 'Error') {
    // promises can reject with null
    const shouldNotify = Bugsnag.isStarted() && this.sendToBugsnag(appError?.message);
    if (shouldNotify) {
      Bugsnag.notify(appError, (event: Event) => {
        // only set the error class if there is only one error
        // multiple errors can occur in one event, but we only want to set the error class for the main error
        if (event.errors.length === 1) {
          event.errors[0].errorClass = errorClass;
        }

        if (metaData) {
          Object.keys(metaData).forEach((section) => {
            event.addMetadata(section, metaData[section]);
          });
        }
      });
    }
  }

  private sendToBugsnag(message?: string) {
    const ignoredMessages = [
      'Email is al in gebruik', // DISABLE_TRANSLATION_CHECK
      'Email address has already been taken on', // DISABLE_TRANSLATION_CHECK
      'Secondary email kan niet identiek zijn aan email', // DISABLE_TRANSLATION_CHECK
      'unique-email', // DISABLE_TRANSLATION_CHECK
      '[Pp]in is invalid', // DISABLE_TRANSLATION_CHECK
      '[Pp]in is ongeldig', // DISABLE_TRANSLATION_CHECK
      'Cannot add duplicate Skill', // DISABLE_TRANSLATION_CHECK
      'Zorg dat de startdatum voor de einddatum ligt', // DISABLE_TRANSLATION_CHECK
      'Voer een titel in', // DISABLE_TRANSLATION_CHECK
      'too late, already running', // comes from the graphs in statistics, not an issue DISABLE_TRANSLATION_CHECK
      'ChunkLoadError', // happens when the FE is released, and browser cache needs updating DISABLE_TRANSLATION_CHECK
      'Loading chunk', // happens when the FE is released, and browser cache needs updating DISABLE_TRANSLATION_CHECK
    ];

    return !ignoredMessages.some((ignoreString) => message?.match(new RegExp(ignoreString, 'gi')));
  }
}
